import React, { useRef, useState } from "react";
import "./ZoomableImage.css";

const MIN_SCALE = 1;
const MAX_SCALE = 4;

function clampScale(value) {
  return Math.max(MIN_SCALE, Math.min(MAX_SCALE, value));
}

export default function ZoomableImage({ src, alt }) {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const pointersRef = useRef(new Map());
  const pinchRef = useRef({ startDist: 0, startScale: 1 });
  const touchGestureRef = useRef({ pinched: false });
  const tapRef = useRef({ time: 0 });
  const stateRef = useRef({ x: 0, y: 0, scale: 1, dragging: false });
  const [state, setState] = useState(stateRef.current);

  const clampTranslate = (x, y, scale) => {
    const container = containerRef.current;
    const img = imgRef.current;
    if (!container || !img) return { x, y };

    const cw = container.clientWidth;
    const ch = container.clientHeight;
    const iw = img.clientWidth;
    const ih = img.clientHeight;

    const scaledW = iw * scale;
    const scaledH = ih * scale;

    const maxX = Math.max(0, (scaledW - cw) / 2);
    const maxY = Math.max(0, (scaledH - ch) / 2);

    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y)),
    };
  };

  const setTransform = (next) => {
    const clamped = clampTranslate(next.x, next.y, next.scale);
    const nextState = {
      ...stateRef.current,
      x: clamped.x,
      y: clamped.y,
      scale: next.scale,
    };
    stateRef.current = nextState;
    setState(nextState);
  };

  const reset = () => {
    const nextState = { ...stateRef.current, x: 0, y: 0, scale: 1 };
    stateRef.current = nextState;
    setState(nextState);
  };

  const onPointerDown = (event) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointersRef.current.size === 1) {
      const nextState = { ...stateRef.current, dragging: true };
      stateRef.current = nextState;
      setState(nextState);
    }

    if (pointersRef.current.size === 2) {
      touchGestureRef.current.pinched = true;
      const points = Array.from(pointersRef.current.values());
      const dx = points[0].x - points[1].x;
      const dy = points[0].y - points[1].y;
      pinchRef.current = {
        startDist: Math.hypot(dx, dy),
        startScale: state.scale,
      };
    }
  };

  const onPointerMove = (event) => {
    if (!pointersRef.current.has(event.pointerId)) return;
    const previous = pointersRef.current.get(event.pointerId);
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointersRef.current.size === 1 && stateRef.current.scale > 1 && previous) {
      const dx = event.clientX - previous.x;
      const dy = event.clientY - previous.y;
      setTransform({
        x: stateRef.current.x + dx,
        y: stateRef.current.y + dy,
        scale: stateRef.current.scale,
      });
    }

    if (pointersRef.current.size === 2) {
      const points = Array.from(pointersRef.current.values());
      const dx = points[0].x - points[1].x;
      const dy = points[0].y - points[1].y;
      const distance = Math.hypot(dx, dy);
      const nextScale = clampScale(
        pinchRef.current.startScale * (distance / pinchRef.current.startDist || 1)
      );
      setTransform({ x: stateRef.current.x, y: stateRef.current.y, scale: nextScale });
    }
  };

  const onPointerUp = (event) => {
    pointersRef.current.delete(event.pointerId);
    if (pointersRef.current.size === 0) {
      const nextState = { ...stateRef.current, dragging: false };
      stateRef.current = nextState;
      setState(nextState);
    }
  };

  const onWheel = (event) => {
    event.preventDefault();
    const direction = event.deltaY < 0 ? 1.1 : 0.9;
    const nextScale = clampScale(stateRef.current.scale * direction);
    setTransform({ x: stateRef.current.x, y: stateRef.current.y, scale: nextScale });
  };

  const onDoubleClick = () => {
    if (stateRef.current.scale > 1) {
      reset();
    } else {
      setTransform({ x: stateRef.current.x, y: stateRef.current.y, scale: 2 });
    }
  };

  const onTouchEnd = () => {
    if (touchGestureRef.current.pinched) {
      touchGestureRef.current.pinched = false;
      tapRef.current.time = 0;
      return;
    }

    const now = Date.now();
    if (now - tapRef.current.time < 260) {
      onDoubleClick();
      tapRef.current.time = 0;
      return;
    }
    tapRef.current.time = now;
  };

  return (
    <div
      className={`zoomable-image-wrap ${state.scale > 1 ? "zoomed" : ""} ${
        state.dragging ? "dragging" : ""
      }`}
      ref={containerRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onWheel={onWheel}
      onDoubleClick={onDoubleClick}
      onTouchEnd={onTouchEnd}
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className="zoomable-image"
        style={{
          transform: `translate(${state.x}px, ${state.y}px) scale(${state.scale})`,
        }}
      />
    </div>
  );
}
